import { createContext, useContext, useEffect, useMemo, useState } from 'react';

import { datadogRum } from '@datadog/browser-rum';
import {
  IAssignmentEvent,
  IAssignmentLogger,
  IBanditEvent,
  IBanditLogger,
  init,
  precomputedInit,
} from '@eppo/js-client-sdk';
import { OpenFeatureProvider } from '@openfeature/react-sdk';
import { OpenFeature, Provider, ClientProviderStatus } from '@openfeature/web-sdk';

import {
  shouldInitializeOpenFeatureClient,
  shouldInitializePrecomputedClient,
  shouldInitializeDatadogProvider,
  useOpenFeatureClientForFeatureFlags,
  shouldWaitForOpenFeatureClientInitialization,
} from 'components/feature-flag/hooks/useFlagAssignment';

import { augmentSubjectContext } from 'lib/augment-subject-attributes';

import { isDatadogRumInitialized, createDatadogOpenFeatureProvider } from 'helpers/datadog-helper';
import { isDevelopmentEnv, isQAEnv } from 'helpers/env-helper';

import { useUser } from 'hooks/useUser';

export const DOGFOOD_BANDIT_FUNCTION_URI_ROOT =
  'https://us-central1-eppo-qa.cloudfunctions.net/java-http-function';

const OVERRIDES_STORAGE_KEY = 'eppo-internal';

interface IEppoFeatureFlagProvider {
  waitForInitialization?: boolean;
  children: JSX.Element;
  loadingComponent?: JSX.Element;
  randomizedTargetingKey?: string | null;
}

export default function EppoFeatureFlagProvider({
  waitForInitialization = false,
  children,
  loadingComponent = <div>Loading...</div>,
  randomizedTargetingKey,
}: IEppoFeatureFlagProvider) {
  const subjectAttributes = useMemo(
    () => ({
      numericAttributes: {},
      categoricalAttributes: {},
    }),
    [],
  );
  const userId = augmentSubjectContext(subjectAttributes, useUser());
  const subjectKey = String(userId);
  const targetingKey = randomizedTargetingKey ?? subjectKey;

  const openFeatureContext = {
    targetingKey,
    ...subjectAttributes.categoricalAttributes,
    ...subjectAttributes.numericAttributes,
  };

  const [hasCompletedInitRequest, setHasCompletedInitRequest] = useState(false);
  const [openFeatureProviderStatus, setOpenFeatureProviderStatus] = useState<ClientProviderStatus>(
    ClientProviderStatus.NOT_READY,
  );
  const [initializationTimeout, setInitializationTimeout] = useState(false);

  const assignmentLogger: IAssignmentLogger = useMemo(
    () => ({
      logAssignment(assignment: IAssignmentEvent) {
        // Send the feature flag when Eppo reports the exposure
        if (isDatadogRumInitialized()) {
          // datadog does not accept `null` values
          datadogRum.addFeatureFlagEvaluation(
            assignment.featureFlag,
            assignment.variation ?? 'NULL',
          );
        }
      },
    }),
    [],
  );

  const banditLogger: IBanditLogger = useMemo(
    () => ({
      logBanditAction(assignment: IBanditEvent) {
        console.debug('logBanditAction', assignment);

        fetch(`${DOGFOOD_BANDIT_FUNCTION_URI_ROOT}/log_bandit_assignment`, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
          },
          body: JSON.stringify({
            featureFlag: assignment.featureFlag,
            bandit: assignment.bandit,
            subject: subjectKey,
            action: assignment.action,
            actionProbability: assignment.actionProbability,
            optimalityGap: assignment.optimalityGap,
            modelVersion: assignment.modelVersion,

            subjectNumericAttributes: subjectAttributes.numericAttributes,
            subjectCategoricalAttributes: subjectAttributes.categoricalAttributes,
            actionNumericAttributes: assignment.actionNumericAttributes,
            actionCategoricalAttributes: assignment.actionCategoricalAttributes,
          }),
        });
      },
    }),
    [],
  );

  useEffect(() => {
    // Set a 500 millisecond timeout as backup to stop waiting for initialization
    const timeoutId = setTimeout(() => {
      setInitializationTimeout(true);
    }, 500);

    // Don't gracefully swallow SDK errors in development and QA environments
    const gracefulFailureMode = !isDevelopmentEnv() && !isQAEnv();

    if (shouldInitializePrecomputedClient && process.env.NEXT_PUBLIC_FEATURE_FLAG_API_KEY) {
      try {
        precomputedInit({
          apiKey: process.env.NEXT_PUBLIC_FEATURE_FLAG_API_KEY,
          enableOverrides: true,
          overridesStorageKey: OVERRIDES_STORAGE_KEY,
          precompute: {
            subjectKey,
            subjectAttributes: {
              ...subjectAttributes.categoricalAttributes,
              ...subjectAttributes.numericAttributes,
            },
            banditActions: {
              'update-highlights-bandit': {
                greenDot: {
                  categoricalAttributes: { type: 'dot', color: 'green' },
                  numericAttributes: {},
                },
                greenText: {
                  categoricalAttributes: { type: 'text', color: 'green' },
                  numericAttributes: {},
                },
                greenBackground: {
                  categoricalAttributes: { type: 'background', color: 'green' },
                  numericAttributes: {},
                },
                emojiSparkle: {
                  categoricalAttributes: { type: 'emoji', color: 'yellow' },
                  numericAttributes: {},
                },
              },
            },
          },
          assignmentLogger,
          banditLogger,
        });
        // eslint-disable-next-line @typescript-eslint/no-unused-vars
        // Since we are conditionally initializing the precomputed client,
        // don't use it to set the completion state.
      } catch (error) {
        datadogRum.addError(error);
        if (gracefulFailureMode) {
          console.warn('Error initializing Eppo SDK', error);
        }
      }
    }

    if (shouldInitializeOpenFeatureClient) {
      OpenFeature.setContext(openFeatureContext)
        .then(() => {
          let provider: Provider | undefined;

          if (shouldInitializeDatadogProvider) {
            provider = createDatadogOpenFeatureProvider() ?? undefined;
          } else {
            console.warn(
              'OpenFeature client enabled but Datadog provider disabled - using default provider',
            );
          }

          if (provider) {
            const ofStartTime = Date.now();
            OpenFeature.setProviderAndWait(provider)
              .then(() => {
                datadogRum.addAction('datadog_sdk_init_duration', {
                  duration: Date.now() - ofStartTime,
                });
                setOpenFeatureProviderStatus(OpenFeature.getClient().providerStatus);
                if (useOpenFeatureClientForFeatureFlags) {
                  return setHasCompletedInitRequest(true);
                }
                return;
              })
              .catch((error) => {
                datadogRum.addError(error);
                console.error('Failed to set provider:', error);
              });
          } else {
            // If no provider is set but OpenFeature client is enabled, mark as ready
            setOpenFeatureProviderStatus(ClientProviderStatus.READY);
          }
          return;
        })
        .catch((error) => {
          datadogRum.addError(error);

          console.error('Failed to initialize provider:', error);
        });
    }

    const startTime = Date.now();
    init({
      apiKey: process.env.NEXT_PUBLIC_FEATURE_FLAG_API_KEY ?? '',
      enableOverrides: true,
      overridesStorageKey: OVERRIDES_STORAGE_KEY,
      assignmentLogger: assignmentLogger,
      banditLogger: banditLogger,
      /*
        This behavior is called "freshest cache on the next session".

        During initialization, the SDK will simultaneously load the cache from disk and
        fetch from the server.

        If there are cached entries found and loading from localStorage wins the race
        (not guaranteed since it is slow and the CDN is fast),
        the init method will return immediately without waiting on the network request
        to complete.

        The user will be served the last session's network loaded configuration for this session.

        Meanwhile, the network request completes and updates the cache. On the next session these
        updated values will be used after they are loaded from the cache.

        This strategy balances the fastest possible SDK initialization while
        relaxing the requirement of having the latest configuration at the start of each session.
        */
      maxCacheAgeSeconds: 0,
      useExpiredCache: true,
      updateOnFetch: 'empty',
    })
      .then((client) => {
        datadogRum.addAction('eppo_sdk_init_duration', { duration: Date.now() - startTime });
        client.setIsGracefulFailureMode(gracefulFailureMode);
        client.useNonExpiringInMemoryAssignmentCache();
        return setHasCompletedInitRequest(true);
      })
      .catch((error) => {
        datadogRum.addError(error);
        if (gracefulFailureMode) {
          console.error('Error initializing Eppo SDK', error);
        }
        return setHasCompletedInitRequest(true);
      });

    // Cleanup timeout on unmount
    return () => {
      clearTimeout(timeoutId);
    };
  }, [subjectKey, subjectAttributes, assignmentLogger, banditLogger]);

  const [providersAreReady, setProvidersAreReady] = useState(false);
  useEffect(() => {
    if (initializationTimeout) {
      setProvidersAreReady(true);
      return;
    }
    setProvidersAreReady(
      hasCompletedInitRequest &&
        (shouldWaitForOpenFeatureClientInitialization
          ? openFeatureProviderStatus === ClientProviderStatus.READY
          : true),
    );
  }, [hasCompletedInitRequest, openFeatureProviderStatus, initializationTimeout]);

  // If OpenFeature is enabled, and we have a provider, wrap children with OpenFeatureProvider
  if (shouldInitializeOpenFeatureClient) {
    return (
      <FeatureFlagContext.Provider
        value={{
          isInitialized: hasCompletedInitRequest,
          isOpenFeatureReady: openFeatureProviderStatus === ClientProviderStatus.READY,
        }}
      >
        <OpenFeatureProvider client={OpenFeature.getClient()}>
          {!waitForInitialization || providersAreReady ? children : loadingComponent}
        </OpenFeatureProvider>
      </FeatureFlagContext.Provider>
    );
  }

  return (
    <FeatureFlagContext.Provider
      value={{
        isInitialized: hasCompletedInitRequest,
        isOpenFeatureReady: false,
      }}
    >
      {!waitForInitialization || hasCompletedInitRequest ? children : loadingComponent}
    </FeatureFlagContext.Provider>
  );
}

// Create a context for the initialization state
export const FeatureFlagContext = createContext({
  isInitialized: false,
  isOpenFeatureReady: false,
});

export function useFeatureFlagInitialized() {
  return useContext(FeatureFlagContext).isInitialized;
}

export function useOpenFeatureInitialized() {
  return useContext(FeatureFlagContext).isOpenFeatureReady;
}
